/**
 * Find the actual scrollable element on the page
 */
function findScrollableElement(): { element: Element | Window; name: string } | null {
    if (typeof window === 'undefined') return null;
    
    // Find ALL elements with overflow-auto, overflow-scroll, or similar
    const allScrollableElements = document.querySelectorAll('*');
    const potentialScrollers: { element: Element; name: string }[] = [];
    
    Array.from(allScrollableElements).forEach((el) => {
        const computedStyle = window.getComputedStyle(el);
        const overflow = computedStyle.overflow;
        const overflowY = computedStyle.overflowY;
        
        if ((overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') 
            && el.scrollHeight > el.clientHeight) {
            potentialScrollers.push({ element: el, name: 'scrollable-element' });
        }
    });
    
    const candidates = [
        ...potentialScrollers,
        { element: document.querySelector('main'), name: 'main' },
        { element: document.body, name: 'body' },
        { element: document.documentElement, name: 'documentElement' },
        { element: window, name: 'window' }
    ];
    
    // First pass: Find element that's actually scrolled
    for (const candidate of candidates) {
        if (!candidate.element) continue;
        
        if (candidate.name === 'window') {
            const scrollY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
            if (scrollY > 0) {
                return { element: window, name: candidate.name };
            }
        } else {
            const element = candidate.element as Element;
            if (element.scrollTop > 0) {
                return { element, name: candidate.name };
            }
        }
    }
    
    // Second pass: Find element with scrollable content
    for (const candidate of candidates) {
        if (!candidate.element) continue;
        
        if (candidate.name === 'window') {
            const scrollHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
            const clientHeight = window.innerHeight;
            if (scrollHeight > clientHeight) {
                return { element: window, name: candidate.name };
            }
        } else {
            const element = candidate.element as Element;
            if (element.scrollHeight > element.clientHeight) {
                return { element, name: candidate.name };
            }
        }
    }
    
    return { element: window, name: 'window' };
}

export function scrollToTop(smooth = true): void {
    if (typeof window === 'undefined') return;
    
    const scrollableTarget = findScrollableElement();
    if (!scrollableTarget) return;
    
    if (scrollableTarget.name === 'window') {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: smooth ? 'smooth' : 'instant'
        });
    } else {
        const element = scrollableTarget.element as Element;
        element.scrollTo({
            top: 0,
            left: 0,
            behavior: smooth ? 'smooth' : 'instant'
        });
    }
}

export function scrollToElement(element: HTMLElement | string, smooth = true, offset = 0): void {
    if (typeof window === 'undefined') return;
    
    let targetElement: HTMLElement | null = null;
    
    if (typeof element === 'string') {
        targetElement = document.querySelector(element);
    } else {
        targetElement = element;
    }
    
    if (!targetElement) return;
    
    const elementPosition = targetElement.offsetTop;
    const offsetPosition = elementPosition - offset;
    
    window.scrollTo({
        top: offsetPosition,
        behavior: smooth ? 'smooth' : 'instant'
    });
}

export function getScrollPosition(): { x: number; y: number } {
    if (typeof window === 'undefined') return { x: 0, y: 0 };
    
    const scrollableTarget = findScrollableElement();
    if (!scrollableTarget) return { x: 0, y: 0 };
    
    if (scrollableTarget.name === 'window') {
        return {
            x: window.pageXOffset || document.documentElement.scrollLeft,
            y: window.pageYOffset || document.documentElement.scrollTop
        };
    } else {
        const element = scrollableTarget.element as Element;
        return {
            x: element.scrollLeft || 0,
            y: element.scrollTop || 0
        };
    }
}

export function isAtTop(threshold = 10): boolean {
    if (typeof window === 'undefined') return true;
    
    return getScrollPosition().y <= threshold;
}